import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {Stack} from '@dropbox/dig-foundations';
import {type ComponentProps, type CSSProperties, Fragment} from 'react';

import dashboardStyles from './Roadmap.module.css';
import {RoadmapProgressBar, type RoadmapProgressBarProps} from './RoadmapProgressBar';
import styles from './RoadmapStatusSummary.module.css';

type SkeletonBoxHeight = ComponentProps<typeof Skeleton.Box>['height'];

const getProgressPercentage = (count: number, total: number) =>
  total === 0 ? 0 : (count / total) * 100;

export const formatProgressPercentage = (count: number, total: number, fractionDigits = 0) =>
  `${getProgressPercentage(count, total).toFixed(fractionDigits)}%`;

export interface RoadmapStatusSummaryCard<TKey extends string = string> {
  key: TKey;
  label: string;
  count: number;
  color: string;
}

interface RoadmapStatusProgressTooltipItem<TKey extends string = string> {
  key: TKey;
  label: string;
  percentage: string;
}

export interface RoadmapStatusProgressTooltipSource<TKey extends string = string> {
  key: TKey;
  label: string;
  count: number;
}

const RoadmapStatusProgressTooltip = <TKey extends string = string>({
  items,
}: {
  items: ReadonlyArray<RoadmapStatusProgressTooltipItem<TKey>>;
}) => (
  <div className={styles.progressTooltip}>
    {items.map((item) => (
      <div key={item.key}>
        {item.label}: {item.percentage}
      </div>
    ))}
  </div>
);

export const getRoadmapStatusProgressTooltip = <TKey extends string = string>({
  items,
  total,
}: {
  items: ReadonlyArray<RoadmapStatusProgressTooltipSource<TKey>>;
  total: number;
}) => {
  if (items.length <= 1) {
    return undefined;
  }

  const visibleItems = items.filter((item) => item.count > 0);
  if (visibleItems.length === 0) {
    return undefined;
  }

  const roundedBucketPercentage = Math.round(
    getProgressPercentage(
      visibleItems.reduce((count, item) => count + item.count, 0),
      total
    )
  );
  const roundedItemPercentageTotal = visibleItems.reduce(
    (percentage, item) => percentage + Math.round(getProgressPercentage(item.count, total)),
    0
  );
  const fractionDigits = roundedBucketPercentage === roundedItemPercentageTotal ? 0 : 1;

  return (
    <RoadmapStatusProgressTooltip
      items={visibleItems.flatMap((item) => {
        const percentage = formatProgressPercentage(item.count, total, fractionDigits);
        if (percentage === '0%' || percentage === '0.0%') {
          return [];
        }
        return [
          {
            key: item.key,
            label: item.label,
            percentage,
          },
        ];
      })}
    />
  );
};

const RoadmapStatusSummaryLabel = ({label}: {label: string}) => {
  const segments = label.split('/');

  return (
    <Text size="small" className={dashboardStyles.dashboardProgressLabel}>
      {segments.length === 1
        ? label
        : segments.map((segment, index) => {
            const hasNextSegment = index < segments.length - 1;
            return (
              <Fragment key={`${segment}-${index}`}>
                <span className={dashboardStyles.dashboardProgressLabelSegment}>
                  {segment}
                  {hasNextSegment ? '/' : null}
                </span>
                {hasNextSegment ? <wbr /> : null}
              </Fragment>
            );
          })}
    </Text>
  );
};

const RoadmapStatusSummaryCardView = <TKey extends string = string>({
  card,
  total,
  isSelected,
  onClick,
}: {
  card: RoadmapStatusSummaryCard<TKey>;
  total: number;
  isSelected?: boolean;
  onClick?: () => void;
}) => {
  const percent = card.key === 'total' ? '100%' : formatProgressPercentage(card.count, total);
  const content = (
    <>
      <RoadmapStatusSummaryLabel label={card.label} />
      <div className={dashboardStyles.dashboardProgressValue} style={{color: card.color}}>
        {card.count} <span className={dashboardStyles.dashboardProgressPercent}>({percent})</span>
      </div>
    </>
  );

  if (!onClick) {
    return <div className={dashboardStyles.dashboardProgressCard}>{content}</div>;
  }

  return (
    <button
      type="button"
      className={dashboardStyles.dashboardProgressCardButton}
      data-progress-bucket={card.key}
      aria-pressed={isSelected}
      onClick={onClick}
    >
      {content}
    </button>
  );
};

export const RoadmapStatusSummary = <TKey extends string = string>({
  cards,
  total,
  selectedCardKey,
  onCardClick,
  statusSummaryAriaLabel,
  progress,
}: {
  cards: ReadonlyArray<RoadmapStatusSummaryCard<TKey>>;
  total: number;
  selectedCardKey?: TKey | null;
  onCardClick?: (key: TKey) => void;
  statusSummaryAriaLabel?: string;
  progress: RoadmapProgressBarProps;
}) => (
  <Stack gap="16">
    <div
      className={dashboardStyles.dashboardProgressStrip}
      role={statusSummaryAriaLabel ? 'group' : undefined}
      aria-label={statusSummaryAriaLabel}
      style={{'--dashboard-progress-column-count': cards.length} as CSSProperties}
    >
      {cards.map((card) => (
        <RoadmapStatusSummaryCardView
          key={card.key}
          card={card}
          total={total}
          isSelected={selectedCardKey === undefined ? undefined : selectedCardKey === card.key}
          onClick={onCardClick ? () => onCardClick(card.key) : undefined}
        />
      ))}
    </div>
    <RoadmapProgressBar {...progress} />
  </Stack>
);

export const RoadmapStatusSummarySkeleton = ({
  testId,
  cardCount = 6,
  progressPlaceholderHeight = 112,
}: {
  testId?: string;
  cardCount?: number;
  progressPlaceholderHeight?: SkeletonBoxHeight;
}) => (
  <Stack gap="16" data-testid={testId}>
    <div
      className={dashboardStyles.dashboardProgressStrip}
      aria-hidden="true"
      style={{'--dashboard-progress-column-count': cardCount} as CSSProperties}
    >
      {Array.from({length: cardCount}, (_, index) => (
        <div key={index} className={dashboardStyles.dashboardProgressCard}>
          <Skeleton.Text width={72} />
          <Skeleton.Text width={48} />
        </div>
      ))}
    </div>
    <Skeleton.Box height={progressPlaceholderHeight} />
  </Stack>
);
